import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, X, Loader2 } from 'lucide-react';

const ValidatedInput = ({
    type = 'text',
    value,
    onChange,
    onStateChange, // Notify parent of validity (true/false)
    onLoading, // Notify parent when validation is in progress
    validator,
    placeholder,
    className = '',
    isTextArea = false,
    dir = undefined,
    prefix = null, // For things like country code selector
    isRTL = false, // Explicit RTL flag for icon positioning
    externalError = null, // Force error from parent
    ...props
}) => {
    const [status, setStatus] = useState('idle'); // idle, typing, valid, invalid
    const [error, setError] = useState(null);

    const validationTimeoutRef = useRef(null);
    const validatorRef = useRef(validator);
    const onStateChangeRef = useRef(onStateChange);
    const onLoadingRef = useRef(onLoading);
    const lastValidatedValue = useRef(value);
    const isInitialMount = useRef(true);

    useEffect(() => { validatorRef.current = validator; }, [validator]);
    useEffect(() => { onStateChangeRef.current = onStateChange; }, [onStateChange]);
    useEffect(() => { onLoadingRef.current = onLoading; }, [onLoading]);

    const performValidation = useCallback((val) => {
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
            validationTimeoutRef.current = null;
        }

        if (!val || val.trim() === '') {
            setStatus('idle');
            setError(null);
            if (onStateChangeRef.current) onStateChangeRef.current(false);
            if (onLoadingRef.current) onLoadingRef.current(false);
            lastValidatedValue.current = val;
            return;
        }

        if (onLoadingRef.current) onLoadingRef.current(true);
        const { valid, error: errMsg } = validatorRef.current(val);
        setStatus(valid ? 'valid' : 'invalid');
        setError(valid ? null : errMsg);
        if (onStateChangeRef.current) onStateChangeRef.current(valid);
        if (onLoadingRef.current) onLoadingRef.current(false);
        lastValidatedValue.current = val;
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (!value) return;
        }

        if (!value || value.trim() === '') {
            setStatus('idle');
            setError(null);
            if (onLoadingRef.current) onLoadingRef.current(false);
            lastValidatedValue.current = value;
            return;
        }

        if (value === lastValidatedValue.current && (status === 'valid' || status === 'invalid')) {
            return;
        }

        setStatus('typing');
        if (onLoadingRef.current) onLoadingRef.current(true);
        if (validationTimeoutRef.current) clearTimeout(validationTimeoutRef.current);

        validationTimeoutRef.current = setTimeout(() => {
            performValidation(value);
        }, 350);

        return () => {
            if (validationTimeoutRef.current) clearTimeout(validationTimeoutRef.current);
        };
    }, [value, performValidation, status]);

    const handleBlur = () => {
        if (value && value.trim() !== '') {
            performValidation(value);
        } else {
            setStatus('idle');
            setError(null);
            if (onLoadingRef.current) onLoadingRef.current(false);
        }
    };

    const handleChange = (e) => {
        onChange(e);
    };

    const getIcon = () => {
        if (status === 'typing') {
            return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
        }
        if (status === 'valid') {
            return (
                <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-green-500" />
                </div>
            );
        }
        if (status === 'invalid' || (status === 'idle' && externalError)) {
            return (
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center">
                    <X className="w-3 h-3 text-red-500" />
                </div>
            );
        }
        return null;
    };

    const borderClass = () => {
        if (status === 'invalid' || (status === 'idle' && externalError)) return 'border-red-500/50 focus:border-red-500';
        if (status === 'valid') return 'border-green-500/50 focus:border-green-500';
        return 'border-slate-700 focus:border-cyan-500'; // Default
    };

    // Determine derived direction
    const derivedDir = dir || (isRTL ? 'rtl' : 'ltr');

    // Padding ensures text doesn't hit the icons
    const paddingClass = isRTL ? 'pl-10 text-right' : 'pr-10 text-left';

    // Base input classes
    const inputClass = `w-full bg-slate-800/50 border rounded-lg focus:outline-none transition-all ${isTextArea ? 'px-4 py-3' : 'px-4 py-3'} ${paddingClass} ${prefix ? 'rounded-l-none border-l-0' : ''} ${className} ${borderClass()}`;

    // Determine icon position class
    const iconPosClass = isRTL ? 'left-3' : 'right-3';

    const activeError = externalError || error;

    return (
        <div className="w-full">
            <div className={`relative w-full ${prefix ? 'flex items-stretch' : ''}`} dir="ltr">
                {prefix && (
                    <div className="flex-shrink-0 flex" dir="ltr">
                        {prefix}
                    </div>
                )}

                <div className="relative w-full">
                    {isTextArea ? (
                        <textarea
                            value={value}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={placeholder}
                            className={inputClass}
                            dir={derivedDir}
                            {...props}
                        />
                    ) : (
                        <input
                            type={type}
                            value={value}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={placeholder}
                            className={inputClass}
                            dir={derivedDir}
                            {...props}
                        />
                    )}

                    <div className={`absolute ${iconPosClass} top-3.5 transition-opacity duration-300 ${(status !== 'idle' || externalError) ? 'opacity-100' : 'opacity-0'}`}>
                        {getIcon()}
                    </div>
                </div>
            </div>

            {/* Cute Error Message */}
            <div
                className={`mt-1.5 px-1 transition-all duration-300 overflow-hidden ${activeError ? 'max-h-10 opacity-100 translate-y-0' : 'max-h-0 opacity-0 -translate-y-2'}`}
            >
                <p
                    className={`text-[11px] italic font-medium text-red-400/90 ${isRTL ? 'text-right' : 'text-left'}`}
                >
                    {activeError}
                </p>
            </div>
        </div>
    );
};

export default ValidatedInput;
